import { FormEvent, useEffect, useMemo, useState } from "react";
import { Building2, Lock, Mail, Phone, Shield, UsersRound, User as UserIcon } from "lucide-react";
import Modal from "../ui/Modal";
import SelectField from "../controls/SelectField";
import MultiSelectField from "../controls/MultiSelectField";
import { SCompanyRow } from "../../types/companies";
import { AdminUserRow, MultiAccountType, UserRole } from "../../types/users";
import { createAdminUser, updateAdminUser } from "../../api/users";
import { formatRuPhone } from "../../utils/authValidation";

type Mode = "create" | "edit";

type Props = {
  open: boolean;
  mode: Mode;
  onClose: () => void;
  onSuccess: (user: AdminUserRow, mode: Mode) => void;
  companies: SCompanyRow[];
  initialUser?: AdminUserRow | null;
};

const ROLE_OPTIONS: { label: string; value: UserRole }[] = [
  { label: "Клиент", value: "client" },
  { label: "Администратор", value: "admin" },
];
const MULTI_ACCOUNT_OPTIONS: { label: string; value: MultiAccountType | "" }[] = [
  { label: "Полный", value: "full" },
  { label: "Частичный", value: "partial" },
];

const NAME_REGEX = /^[А-Яа-яЁё\s]+$/u;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AdminUserModal({
  open,
  mode,
  onClose,
  onSuccess,
  companies,
  initialUser,
}: Props) {
  const [fullname, setFullname] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState<UserRole>("client");
  const [companyId, setCompanyId] = useState("");
  const [multiAccountType, setMultiAccountType] = useState<MultiAccountType | "">("");
  const [accessibleCompanyIds, setAccessibleCompanyIds] = useState<string[]>([]);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const baseInputClass =
    "w-full rounded-2xl border border-slate-200/80 bg-white/80 px-4 py-[14px] text-[15px] text-slate-900 placeholder:text-slate-400 shadow-inner transition focus:border-indigo-300 focus:ring-4 focus:ring-indigo-200/60 disabled:opacity-60 disabled:cursor-not-allowed";

  useEffect(() => {
    if (!open) return;

    setFullname(initialUser?.fullname ?? "");
    setEmail(initialUser?.email ?? "");
    setPhone(initialUser?.phone ?? "");
    setRole(initialUser?.role ?? "client");
    setMultiAccountType(initialUser?.multi_account_type ?? "");
    setAccessibleCompanyIds(
      initialUser?.accessible_company_ids?.map(String) ??
      initialUser?.accessible_companies?.map((company) => String(company.id)) ??
      [],
    );
    setCompanyId(
      initialUser?.company_id && initialUser.role === "client" && initialUser.multi_account_type !== "partial"
        ? String(initialUser.company_id)
        : "",
    );
    setPassword("");
    setPasswordConfirm("");
    setError("");
    setSubmitting(false);
  }, [open, initialUser, mode]);

  useEffect(() => {
    if (role === "admin" || multiAccountType) {
      setCompanyId("");
    }
    if (role === "admin") {
      setMultiAccountType("");
      setAccessibleCompanyIds([]);
    }
  }, [multiAccountType, role]);

  useEffect(() => {
    if (multiAccountType === "full") {
      setRole("client");
      setAccessibleCompanyIds([]);
    }
    if (multiAccountType === "partial") {
      setRole("client");
    }
  }, [multiAccountType]);

  const companyOptions = useMemo(
    () =>
      companies
        .filter((company) => !company.deleted_at)
        .map((company) => ({
          label: company.company_name,
          value: String(company.id),
        })),
    [companies],
  );

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;

    const trimmedFullname = fullname.trim();
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedPhone = phone.trim();
    const trimmedPassword = password.trim();
    const trimmedPasswordConfirm = passwordConfirm.trim();

    if (!trimmedFullname) {
      return setError("Введите ФИО");
    }
    if (!NAME_REGEX.test(trimmedFullname)) {
      return setError("ФИО может содержать только буквы и пробелы");
    }
    if (!trimmedEmail) {
      return setError("Введите email");
    }
    if (!EMAIL_REGEX.test(trimmedEmail)) {
      return setError("Некорректный email");
    }
    if (!role) {
      return setError("Выберите роль");
    }
    if (multiAccountType === "partial" && accessibleCompanyIds.length === 0) {
      return setError("Выберите доступные компании для частичного мультиаккаунта");
    }
    if (role === "client" && !multiAccountType && !companyId) {
      return setError("Выберите компанию");
    }

    if (mode === "create") {
      if (!trimmedPassword) {
        return setError("Введите пароль");
      }
      if (trimmedPassword.length < 8 || !/\d/.test(trimmedPassword)) {
        return setError("Пароль должен быть не короче 8 символов и содержать цифры");
      }
      if (trimmedPassword !== trimmedPasswordConfirm) {
        return setError("Пароли не совпадают");
      }
    } else if (trimmedPassword || trimmedPasswordConfirm) {
      if (trimmedPassword.length < 8 || !/\d/.test(trimmedPassword)) {
        return setError("Новый пароль должен быть не короче 8 символов и содержать цифры");
      }
      if (trimmedPassword !== trimmedPasswordConfirm) {
        return setError("Пароли не совпадают");
      }
    }

    setSubmitting(true);
    setError("");

    try {
      const effectiveRole: UserRole = multiAccountType ? "client" : role;
      const payloadMultiAccountType = multiAccountType || null;
      const payloadAccessibleCompanyIds = multiAccountType === "partial"
        ? accessibleCompanyIds.map(Number)
        : [];
      const payloadCompanyId = effectiveRole === "client" && !multiAccountType ? Number(companyId) : null;

      if (mode === "create") {
        const created = await createAdminUser({
          fullname: trimmedFullname,
          email: trimmedEmail,
          phone: trimmedPhone || null,
          password: trimmedPassword,
          role: effectiveRole,
          company_id: payloadCompanyId,
          multi_account_type: payloadMultiAccountType,
          accessible_company_ids: payloadAccessibleCompanyIds,
        });
        onSuccess(created, mode);
        onClose();
      } else if (initialUser) {
        const payload: Record<string, any> = {};

        if (trimmedFullname !== initialUser.fullname) {
          payload.fullname = trimmedFullname;
        }
        if (trimmedEmail !== initialUser.email) {
          payload.email = trimmedEmail;
        }
        if (trimmedPhone !== (initialUser.phone ?? "")) {
          payload.phone = trimmedPhone || null;
        }
        if (effectiveRole !== initialUser.role) {
          payload.role = effectiveRole;
        }
        if (payloadCompanyId !== initialUser.company_id) {
          payload.company_id = payloadCompanyId;
        }
        if (payloadMultiAccountType !== (initialUser.multi_account_type ?? null)) {
          payload.multi_account_type = payloadMultiAccountType;
        }
        const initialAccessibleIds = (
          initialUser.accessible_company_ids ??
          initialUser.accessible_companies?.map((company) => company.id) ??
          []
        ).slice().sort((left, right) => left - right);
        const nextAccessibleIds = payloadAccessibleCompanyIds.slice().sort((left, right) => left - right);
        const accessibleChanged =
          initialAccessibleIds.length !== nextAccessibleIds.length ||
          initialAccessibleIds.some((id, index) => id !== nextAccessibleIds[index]);
        if (accessibleChanged || payloadMultiAccountType !== (initialUser.multi_account_type ?? null)) {
          payload.accessible_company_ids = payloadAccessibleCompanyIds;
        }
        if (trimmedPassword) {
          payload.password = trimmedPassword;
        }

        if (Object.keys(payload).length === 0) {
          onSuccess(initialUser, mode);
          onClose();
          return;
        }

        const updated = await updateAdminUser(initialUser.id, payload);
        onSuccess(updated, mode);
        onClose();
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Не удалось сохранить пользователя");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!submitting) onClose();
      }}
      title=""
      widthClass="max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="text-center space-y-3 animate-softFade">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-600 shadow-inner">
            <UserIcon size={24} />
          </div>
          <div>
            <h3 className="text-2xl font-semibold text-slate-900">
              {mode === "create" ? "Добавить пользователя" : "Редактировать пользователя"}
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Заполните данные для доступа к платформе
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">ФИО *</span>
            <div className="relative">
              <UserIcon size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={fullname}
                onChange={(event) => setFullname(event.target.value)}
                maxLength={150}
                placeholder="Введите ФИО"
                disabled={submitting}
                className={`${baseInputClass} pl-11 pr-4`}
              />
            </div>
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Email *</span>
            <div className="relative">
              <Mail size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="user@example.com"
                disabled={submitting}
                className={`${baseInputClass} pl-11 pr-4`}
              />
            </div>
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Телефон</span>
            <div className="relative">
              <Phone size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={phone}
                onChange={(event) => setPhone(formatRuPhone(event.target.value))}
                placeholder="+7 (___) ___-__-__"
                inputMode="tel"
                autoComplete="tel"
                disabled={submitting}
                className={`${baseInputClass} pl-11 pr-4`}
              />
            </div>
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block space-y-2">
              <span className="text-sm font-semibold text-slate-600">Роль *</span>
              <SelectField
                value={role}
                onChange={(value) => setRole(value as UserRole)}
                options={ROLE_OPTIONS.map((option) => ({
                  label: option.label,
                  value: option.value,
                }))}
                placeholder="Выберите роль"
                icon={<Shield size={18} />}
                className="border-slate-200/80 bg-white/80 shadow-inner"
                disabled={submitting}
              />
            </label>

            {role === "client" && (
              <label className="block space-y-2">
                <span className="text-sm font-semibold text-slate-600">Тип мультиаккаунта</span>
                <SelectField
                  value={multiAccountType}
                  onChange={(value) => setMultiAccountType(value as MultiAccountType | "")}
                  options={MULTI_ACCOUNT_OPTIONS.map((option) => ({
                    label: option.label,
                    value: option.value,
                  }))}
                  placeholder="Без мультиаккаунта"
                  icon={<UsersRound size={18} />}
                  className="border-slate-200/80 bg-white/80 shadow-inner"
                  disabled={submitting}
                />
              </label>
            )}

            {role === "client" && !multiAccountType && (
              <label className="block space-y-2">
                <span className="text-sm font-semibold text-slate-600">Компания *</span>
                <SelectField
                  value={companyId}
                  onChange={(value) => setCompanyId(String(value))}
                  options={companyOptions}
                  placeholder="Выберите компанию"
                  icon={<Building2 size={18} />}
                  className="border-slate-200/80 bg-white/80 shadow-inner"
                  disabled={submitting}
                />
              </label>
            )}

            {multiAccountType === "partial" && (
              <label className="block space-y-2 sm:col-span-2">
                <span className="text-sm font-semibold text-slate-600">Доступные компании *</span>
                <MultiSelectField
                  value={accessibleCompanyIds}
                  onChange={setAccessibleCompanyIds}
                  options={companyOptions}
                  placeholder="Выберите компании"
                  icon={<Building2 size={18} />}
                  className="border-slate-200/80 bg-white/80 shadow-inner"
                  disabled={submitting}
                />
              </label>
            )}
          </div>

          {(mode === "create" || role === "client" || mode === "edit") && (
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block space-y-2">
                <span className="text-sm font-semibold text-slate-600">
                  {mode === "create" ? "Пароль *" : "Новый пароль"}
                </span>
                <div className="relative">
                  <Lock size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder={mode === "create" ? "Минимум 8 символов" : "Оставьте пустым, чтобы не менять"}
                    disabled={submitting}
                    className={`${baseInputClass} pl-11 pr-4`}
                  />
                </div>
              </label>

              <label className="block space-y-2">
                <span className="text-sm font-semibold text-slate-600">
                  {mode === "create" ? "Подтверждение пароля *" : "Подтверждение пароля"}
                </span>
                <div className="relative">
                  <Lock size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="password"
                    value={passwordConfirm}
                    onChange={(event) => setPasswordConfirm(event.target.value)}
                    placeholder="Повторите пароль"
                    disabled={submitting}
                    className={`${baseInputClass} pl-11 pr-4`}
                  />
                </div>
              </label>
            </div>
          )}
        </div>

        {error && (
          <div className="animate-softFade rounded-2xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-800 shadow-inner">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-center">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex flex-1 items-center justify-center rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-[0_18px_30px_rgba(99,102,241,0.35)] transition hover:translate-y-[-1px] hover:brightness-105 disabled:opacity-50 sm:flex-none"
          >
            {submitting ? "Сохраняем…" : mode === "create" ? "Добавить" : "Сохранить"}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="inline-flex flex-1 items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50 sm:flex-none"
          >
            Отмена
          </button>
        </div>
      </form>
    </Modal>
  );
}
